import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  Briefcase,
  Calendar,
  ChevronLeft,
  Mail,
  Package,
  Phone,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import bookingsIllustration from "../../Assets/Images/Bookings/bookings_illustration.png";
import logo from "../../Assets/Images/FooterImages/Logo1.webp";
import StatusBadge from "../../components/common/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { formatDate } from "../../utils/helpers";

// helpers 
const mergeAndSort = (serviceBookings, packageBookings) => {
  const services = (serviceBookings || []).map((b) => ({
    ...b,
    _bookingType: "service",
  }));
  const packages = (packageBookings || []).map((b) => ({
    ...b,
    _bookingType: "package",
  }));
  return [...services, ...packages].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const UserBookings = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!user?._id) return;

    const fetchBookings = async () => {
      setLoading(true);
      try {
        const [serviceRes, packageRes] = await Promise.allSettled([
          axios.get(
            `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-user-id/${user._id}`,
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/package-bookings/user/${user._id}`,
          ),
        ]);

        const serviceData =
          serviceRes.status === "fulfilled"
            ? serviceRes.value.data?.data || []
            : [];
        const packageData =
          packageRes.status === "fulfilled"
            ? packageRes.value.data?.data || []
            : [];

        setBookings(mergeAndSort(serviceData, packageData));
      } catch (err) {
        console.error(err);
        setError("Failed to load bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user?._id]);

  const handleStaffClick = (staff) => {
    setSelectedStaff(staff);
    setShowStaffModal(true);
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <BookingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex bg-primaryColor/10 backdrop-blur border-b border-primaryColor/10 items-center gap-1 px-3 py-3 lg:py-5 shadow-sm">
        <button onClick={() => navigate(-1)} className="p-1">
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-base font-bold capitalize">Bookings</h1>
      </div>

      <div className="h-screen lg:h-[76vh] flex items-center justify-center overflow-hidden relative bg-brandSurface">
        {/* LEFT — Bookings list */}
        <div className="h-full w-full lg:w-[60%] flex flex-col max-w-3xl mx-auto no-scrollbar py-4 px-4 lg:py-8 space-y-4 overflow-y-auto pb-20">
          {!loading && bookings.length === 0 && (
            <EmptyBookingsState onBack={() => navigate(-1)} />
          )}

          {!loading &&
            bookings.map((booking) => {
              const isPackage = booking._bookingType === "package";

              const displayDate =
                booking.rescheduledDate || booking.slotAvailabilityId?.date;

              const totalAmount = booking?.totalAmount ?? 0;
              const manualDiscount = booking?.manualDiscount ?? 0;
              const couponDiscount = booking?.coupon?.discountApplied ?? 0;

              const totalDiscount = manualDiscount + couponDiscount;

              const finalPayable = booking?.finalPayableAmount ?? totalAmount;

              const hasDiscount = totalDiscount > 0;

              const discountPct =
                totalAmount > 0 && totalDiscount > 0
                  ? ((totalDiscount / totalAmount) * 100).toFixed(0)
                  : "0";

              // Service label
              const serviceLabel = isPackage
                ? booking.packageSnapshot?.name || "Package"
                : (booking.services || [])
                  .map((s) => s.serviceId?.name)
                  .filter(Boolean)
                  .join(", ");

              return (
                <div
                  key={booking._id}
                  onClick={() =>
                    isPackage
                      ? navigate(`/my-bookings/package/${booking._id}`)
                      : navigate(`/my-bookings/service/${booking._id}`)
                  }
                  className="bg-white border rounded-xl md:rounded-2xl p-3 md:p-4 shadow-sm space-y-3 cursor-pointer hover:shadow-lg hover:border-blue-300 active:scale-[0.99] transition-all"
                >
                  {/* Header row */}
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={booking.status} />
                      {isPackage && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
                          <Package size={10} />
                          Package
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] md:text-xs text-gray-500">
                      {formatDate(booking.createdAt)}
                    </span>
                  </div>

                  {/* Service / Package name */}
                  <div className="text-sm md:text-base text-gray-800 font-medium line-clamp-2">
                    {serviceLabel}
                  </div>

                  {/* Slot */}
                  <div className="text-xs md:text-sm text-gray-600 flex items-center gap-1">
                    <Calendar size={14} className="flex-shrink-0" />
                    <span>
                      {formatDate(displayDate)} ·{" "}
                      {booking.slotSnapshot?.startTime} –{" "}
                      {booking.slotSnapshot?.endTime}
                    </span>
                  </div>

                  {/* Staff */}
                  {booking.staffId && (
                    <>
                      <p className="text-sm text-secondaryTextColor">
                        Assigned staff
                      </p>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStaffClick(booking.staffId);
                        }}
                        className="flex items-center gap-2.5 p-2.5 md:p-3 bg-blue-50 hover:bg-blue-100 active:bg-blue-100 rounded-lg transition cursor-pointer"
                      >
                        {booking.staffId.profilePicture ? (
                          <img
                            src={booking.staffId.profilePicture}
                            alt={booking.staffId.name}
                            className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor text-white flex items-center justify-center text-sm md:text-base font-bold border-2 border-white shadow-sm">
                            {booking.staffId.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm font-semibold text-gray-800 truncate">
                            {booking.staffId.name}
                          </p>
                          <p className="text-[10px] md:text-xs text-blue-600 font-medium">
                            {booking.staffId.designation || "Staff Member"}
                          </p>
                        </div>
                        <div className="text-blue-600 text-xs font-medium hidden sm:block">
                          View Profile →
                        </div>
                      </div>
                    </>
                  )}

                  {/* Cancel reason */}
                  {booking.status === "cancelled" && booking.cancelReason && (
                    <div className="text-xs md:text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                      <span className="font-medium">Reason:</span>{" "}
                      {booking.cancelReason}
                    </div>
                  )}

                  {/* Amount */}
                  <div className="pt-2 border-t space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs md:text-sm text-gray-500">
                        {hasDiscount ? "Original Price" : "Total Amount"}
                      </span>
                      <span
                        className={`text-sm md:text-base font-semibold ${hasDiscount
                          ? "line-through text-gray-400"
                          : "text-gray-800 font-bold"
                          }`}
                      >
                        ₹{totalAmount.toLocaleString("en-IN")}
                      </span>
                    </div>

                    {hasDiscount && (
                      <div className="flex justify-between items-center text-green-600 text-xs md:text-sm">
                        <span>
                          {booking?.coupon?.code && (
                            <p className="text-[11px] text-green-600">
                              Coupon applied: {booking.coupon.code}

                              {" "}({discountPct}% OFF)
                            </p>
                          )}
                        </span>
                        <span>
                          -₹{totalDiscount.toLocaleString("en-IN")}                        </span>
                      </div>
                    )}


                    {booking?.payment?.status === "partially_paid" ? (
                      <>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Paid</span>
                          <span>
                            ₹
                            {booking.payment.paidAmount?.toLocaleString(
                              "en-IN",
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm text-red-600 font-semibold">
                          <span>Due</span>
                          <span>
                            ₹
                            {booking.payment.remainingAmount?.toLocaleString(
                              "en-IN",
                            )}
                          </span>
                        </div>
                      </>
                    ) : booking?.payment?.status === "paid" ? (
                      <div className="flex justify-between text-sm text-green-600 font-semibold">
                        <span>Paid</span>
                        <span>₹{finalPayable.toLocaleString("en-IN")}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-xs md:text-sm font-medium text-gray-700">
                          Final Payable
                        </span>
                        <span className="text-base md:text-lg font-bold text-primaryColor">
                          ₹{finalPayable.toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        {/* RIGHT — illustration */}
        <div className="w-[40%] hidden lg:block h-full relative">
          <img
            src={bookingsIllustration}
            alt="Bookings illustration"
            className="absolute bottom-0 right-1/2 translate-x-1/2 w-[30rem] pointer-events-none select-none z-10"
          />
        </div>
      </div>

      {/* Staff Profile Modal */}
      <StaffProfileModal
        isOpen={showStaffModal}
        staff={selectedStaff}
        onClose={() => {
          setShowStaffModal(false);
          setSelectedStaff(null);
        }}
      />
    </div>
  );
};

// ─── Staff Profile Modal ──────────────────────────────────────────────────────
const StaffProfileModal = ({ isOpen, staff, onClose }) => {
  if (!staff) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Mobile bottom sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col md:hidden"
          >
            <div className="flex justify-center py-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>
            <div className="flex-1 overflow-y-auto">
              <StaffProfileContent staff={staff} onClose={onClose} />
            </div>
          </motion.div>

          {/* Desktop centered modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="hidden md:flex fixed inset-0 items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
              <StaffProfileContent staff={staff} onClose={onClose} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const StaffProfileContent = ({ staff, onClose }) => (
  <>
    <div className="sticky top-0 shadow-xl bg-white">
      <div
        style={{ backgroundImage: `url(${logo})` }}
        className="h-24 md:h-28 bg-contain bg-center bg-no-repeat opacity-20"
      />
      <button
        onClick={onClose}
        className="absolute top-3 right-3 w-8 h-8 md:w-9 md:h-9 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition shadow-lg active:scale-95"
      >
        <X size={18} className="text-gray-700" />
      </button>
      <div className="absolute -bottom-12 md:-bottom-14 left-1/2 -translate-x-1/2">
        {staff.profilePicture ? (
          <img
            src={staff.profilePicture}
            alt={staff.name}
            className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-xl object-cover"
          />
        ) : (
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor text-white flex items-center justify-center text-3xl md:text-4xl font-bold">
            {staff.name?.charAt(0)?.toUpperCase()}
          </div>
        )}
      </div>
    </div>

    <div className="pt-14 md:pt-16 px-4 md:px-6 pb-6 overflow-y-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
          {staff.name}
        </h2>
        <p className="text-sm md:text-base text-blue-600 font-medium">
          {staff.designation || "Staff Member"}
        </p>
        {staff.gender && (
          <span className="inline-block mt-2 px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            {staff.gender.charAt(0).toUpperCase() + staff.gender.slice(1)}
          </span>
        )}
      </div>

      <div className="space-y-3">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-3 md:p-4">
          <h3 className="text-xs md:text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <Mail size={12} className="text-white" />
            </div>
            Contact Details
          </h3>
          <div className="space-y-2.5">
            <InfoItem icon={<Mail size={14} />} label="Email" value={staff.email} />
            <InfoItem icon={<Phone size={14} />} label="Phone" value={staff.phoneNumber} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-3 md:p-4">
          <h3 className="text-xs md:text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <Briefcase size={12} className="text-white" />
            </div>
            Professional Info
          </h3>
          <div className="space-y-2.5">
            <InfoItem
              icon={<Briefcase size={14} />}
              label="Role"
              value={staff.userType === "staff" ? "Staff Member" : staff.userType}
            />
            {staff.designation && (
              <InfoItem
                icon={<Briefcase size={14} />}
                label="Designation"
                value={staff.designation}
              />
            )}
          </div>
        </div>
      </div>

      <button
        onClick={onClose}
        className="w-full mt-6 py-3 md:py-3.5 bg-primaryBtnColor text-white font-semibold rounded-xl hover:bg-primaryBtnHoverColor active:scale-[0.98] transition shadow-lg"
      >
        Close
      </button>
    </div>
  </>
);

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white/80 flex items-center justify-center text-gray-600 flex-shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] md:text-xs text-gray-600 font-medium">{label}</p>
      <p className="text-xs md:text-sm font-semibold text-gray-800 break-words mt-0.5">
        {value}
      </p>
    </div>
  </div>
);

const EmptyBookingsState = ({ onBack }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-4">
    <img
      src={bookingsIllustration}
      alt="No bookings"
      className="w-64 opacity-80 mb-6 select-none pointer-events-none"
    />
    <h2 className="text-lg font-semibold text-gray-800">No bookings yet</h2>
    <p className="text-sm text-gray-500 mt-1 max-w-xs">
      Once you book a service, all your upcoming and past bookings will appear here.
    </p>
    <button
      onClick={onBack}
      className="mt-5 px-4 py-2 text-sm font-medium rounded-lg bg-primaryColor text-white hover:opacity-90 transition"
    >
      Explore Services
    </button>
  </div>
);

const BookingSkeleton = () => (
  <div className="bg-white border max-w-3xl mx-auto rounded-xl p-4 shadow-sm space-y-3 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="h-4 w-20 bg-gray-200 rounded" />
      <div className="h-3 w-16 bg-gray-200 rounded" />
    </div>
    <div className="h-4 w-3/4 bg-gray-200 rounded" />
    <div className="h-3 w-1/2 bg-gray-200 rounded" />
    <div className="flex items-center gap-2.5 p-3 bg-gray-100 rounded-lg">
      <div className="w-12 h-12 bg-gray-200 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-24 bg-gray-200 rounded" />
        <div className="h-2 w-16 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="pt-2 border-t">
      <div className="h-4 w-24 bg-gray-200 rounded" />
    </div>
  </div>
);

export default UserBookings;

